import { Navigate, useNavigate } from 'react-router';
import { Banner, Button } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import { ShellSkeleton } from '../../../shell/ShellSkeleton';
import { useUserSetup } from './api';
import styles from './DonePage.module.css';

/**
 * `/setup/user/done` — Onboarding Nutzer 12: „Sie können starten, <Vorname Nachname>." (D-73) with the done and open
 * lines of `GET /setup/user`, „Zur Checkliste" / „DAMOKLES öffnen". Before the 2FA step the wizard resumes instead
 * (with optional 2FA, D-338, only while the step is still open — a skipped one counts as answered).
 */
export function DonePage() {
  const navigate = useNavigate();
  const session = useSession();
  const setup = useUserSetup();

  if (setup.isError) {
    return (
      <div className={styles.page}>
        <div className={styles.column}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void setup.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(setup.error)}
          </Banner>
        </div>
      </div>
    );
  }
  if (!setup.data) return <ShellSkeleton />;
  const security = setup.data.steps.find((step) => step.key === 'security');
  const securityPending = session.twoFactorRequired
    ? security?.status !== 'done'
    : security?.status === 'open';
  if (security && securityPending) return <Navigate to="/setup/user/security" replace />;

  const { summary } = setup.data;
  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <span className={styles.icon} aria-hidden="true">
          ✓
        </span>
        <div className={styles.text}>
          <h1 className={styles.headline}>{summary.headline}</h1>
          <p className={styles.subline}>{summary.subline}</p>
        </div>
        <div className={styles.lines}>
          {summary.doneLine ? (
            <div className={styles.line}>
              <span className={styles.doneMark} aria-hidden="true">
                ✓
              </span>
              {summary.doneLine.replace(/^✓\s*/, '')}
            </div>
          ) : null}
          {summary.openLine ? (
            <div className={`${styles.line} ${styles.lineOpen}`}>
              <span className={styles.openCount} aria-hidden="true">
                {summary.openLabels.length}
              </span>
              {summary.openLine}
            </div>
          ) : null}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={() => void navigate('/setup/checklist')}
          >
            Zur Checkliste
          </button>
          <button type="button" className={styles.primary} onClick={() => void navigate('/tasks')}>
            DAMOKLES öffnen
          </button>
        </div>
      </div>
    </main>
  );
}
